import React from 'react'
import cancelable from 'react-disposable-decorator'
import { catchSyncStacktrace, asyncStacktrace } from 'auto-trace'
import { get, noop } from 'lodash'
import { defer, pipe, from, concat } from 'rxjs'
import { tap, first } from 'rxjs/operators'
import queryString from 'query-string'
import {track} from 'cp-analytics';
// sofe imports
import { CprButton, CprLoader, CpButton } from 'canopy-styleguide!sofe'
import { onPusher } from 'fetcher!sofe'
import Auth from 'cp-client-auth!sofe'
// project imports
import { resendEmail, loginAfterMFA } from './email-sent.resource.js'
import { obfuscateEmail, maybeShowRememberPrompt } from './email-sent.helper.js'
import CheckAuthCookieAndRefresh from 'src/login/check-auth-cookie-and-refresh/check-auth-cookie-and-refresh.component.js';
import styles from './email-sent.styles.css';
import { successToast } from 'toast-service!sofe';
import { ContentWrapperOnly } from 'src/mobile/mobile-wrapper/mobile-wrapper.component.js'

@cancelable
export default class MFAEmailSent extends React.PureComponent {

  constructor(props) {
    super(props)

    const queryParams = queryString.parse(this.props.location.search)

    this.state = {
      emailSentAgain: false,
      invalidCredentialsCookie: false,
      email: get(window, 'history.state.email') || queryParams.email || get(this ,'props.location.state.email'),
      channelId: get(window, 'history.state.channelId') || queryParams.channelId || get(this ,'props.location.state.channelId'),
      loadingApp: false,
      redirectUrl: null,
      checkComplete: get(this ,'props.location.state.checkComplete'),
    }
  }
  componentDidMount() {
    if (this.state.channelId) {
      track('practice_management', 'directory', 'login_mfa_pending')

      this.props.cancelWhenUnmounted(
        //do a quick check to see if this device is allowed/denied already
        this.mfaObservableSubscribe(
          concat(
            loginAfterMFA(this.state.channelId),
            defer(Auth.refetchLoggedInUser),
            defer(Auth.refetchTenant),
          )
        ),
        //subscribe to the pusher
        this.mfaObservableSubscribe(
          concat(
            onPusher('mfa-login', this.state.channelId).pipe(
              first(),
              tap(() => {
                this.setState({loadingApp: true})
              }),
            ),
            loginAfterMFA(this.state.channelId),
            defer(Auth.refetchLoggedInUser),
            defer(Auth.refetchTenant),
          )
        )
      )
    } else {
      this.props.history.push('/login')
    }
  }

  mfaObservableSubscribe(observ) {
    return observ
    .subscribe(
      noop,
      (err) => {
        if (err.status === 403) {
          this.setState({loadingApp: false})
          this.props.history.push(`/login/mfa/device-denied`)
        } else if (err.status === 401) {
          this.setState({invalidCredentialsCookie: true, loadingApp: false})
        } else if (err.status === 404) {
          //not allowed or denied yet -- still just waiting for the decision from the email but handle it here so it doesn't pop a toaster
          if(this.state.emailSentAgain) {
            //OR there were multiple emails sent, and they opened/clicked on an older one. only the most recent email is valid, so this could be called in that case
            //so set loading to false again so that they don't just see a loader forever
            this.setState({loadingApp: false})
          }
        } else {
          catchSyncStacktrace(err)
        }
      },
      () => {
        const parsed = queryString.parse(this.props.location.search);
        const redirectUrl = parsed.redirect_url;

        maybeShowRememberPrompt()

        track('practice_management', 'directory', 'login_mfa_verified')

        if (redirectUrl) {
          if (this.props.mobile) {
            this.setState({redirectUrl});
          } else {
            window.location.href = redirectUrl
          }
        } else {
          this.props.history.push('/')
        }
      }
    )
  }

  render() {
    return (
      <div>
        {this.renderContent()}
      </div>
    )
  }

  renderContent = () => {
    if (this.state.redirectUrl && this.props.mobile) {
      return this.renderAccountVerifiedMobile();
    } else if (this.state.loadingApp) {
      return <CprLoader />;
    } else {
      if (this.state.invalidCredentialsCookie) {
        return this.props.mobile ? this.renderTimeoutMobile() : this.renderTimeoutDesktop();
      } else {
        return (
          <div>
            <CheckAuthCookieAndRefresh location={this.props.location} />
            {this.props.mobile ? this.renderEmailSentMobile() : this.renderEmailSentDesktop()}
          </div>
        )
      }
    }
  }

  renderAccountVerifiedMobile = () => {
    return <ContentWrapperOnly>
      <div className="cps-text-center email-sent-mobile-view-identifier">
        <img
          src='https://cdn.canopytax.com/static/login-ui/verification-email.svg'
          alt="Verification Email"
        />
        <div className={`cps-title cps-wt-semibold cps-margin-top-32`}>
          Account verified!
        </div>
      </div>
      <div className={`cps-padding-bottom-16`}>
        <CprButton
          className={`${styles.mobileButton}`}
          actionType="primary"
          onClick={ () => { window.location.href = this.state.redirectUrl } }
          disableOnClick={ true }
        >
          Back to Canopy
        </CprButton>
      </div>
    </ContentWrapperOnly>
  }

  renderEmailSentMobile = () => {
    const obfuscatedEmail = this.state.email && obfuscateEmail(this.state.email)
    return (
      <ContentWrapperOnly>
        <div className="cps-text-center email-sent-mobile-view-identifier">
          <img
            src='https://cdn.canopytax.com/static/login-ui/verification-email.svg'
            alt="Verification Email"
          />
          <div className={`cps-title cps-wt-semibold cps-margin-top-32`}>
            {this.state.checkComplete ?
              `We're still waiting on your verification` :
              `We've sent you a verification email`
            }
          </div>
          <div className={`cps-subheader-sm cps-margin-top-32`}>
            {this.state.checkComplete ?
              `Please follow all of the instructions on the email that we've sent${this.state.email ? ` to ${obfuscatedEmail}`: ''}`
              : 'To make sure your account is secure, we need to verify your identity.'
            }
          </div>
          {this.state.email && !this.state.checkComplete && (
              <div>
                <div className={`cps-subheader-sm cps-margin-top-32`}>
                  Please follow the instructions on the email that we've sent to
                </div>
                <div className={`cps-subheader-sm`}>
                  {obfuscatedEmail}
                </div>
              </div>
            )
          }
        </div>
        <div className={styles.mobileActions}>
          <CpButton
            className={styles.mobileButton}
            btnType="primary"
            actionType="secondary"
            onClick={ () => {
              const path = this.props.location
              if (!this.state.checkComplete) {
                const state = {
                  channelId: this.state.channelId,
                  email: this.state.email,
                  checkComplete: true,
                }
                this.props.history.replace(`${this.props.history.location.pathname}${this.props.history.location.search}`, state)
              }
              this.props.history.go(0)
            }}
            disableOnClick={ true }
          >
            I've completed the verification
          </CpButton>
          <CpButton
            className={styles.mobileButton}
            className='cp-mt-16'
            btnType="flat"
            actionType="secondary"
            onClick={ this.resendEmailHandler }
            disableOnClick={ true }
          >
            Resend email
          </CpButton>
        </div>
      </ContentWrapperOnly>
    )
  }

  renderEmailSentDesktop = () => {
    return (
      <React.Fragment>
        <div className={`cps-padding-bottom-16`}>
          To make sure your account is secure, we need to verify your identity.
        </div>
        {
          this.state.email && (
            <div>
              <div className={`cps-padding-bottom-16`}>
                We emailed a verification link to the below email address.
              </div>
              <div className={`cps-padding-bottom-16`}>
                {obfuscateEmail(this.state.email)}
              </div>
            </div>
          )
        }
        <div className={`cps-padding-bottom-16`}>
          Please follow the intructions in your email to access your account.
        </div>
        <div className={`cps-padding-bottom-16`}>
          <CprButton
            actionType="flat"
            onClick={ this.resendEmailHandler }
            disableOnClick={ true }
          >
            Resend Link?
          </CprButton>
        </div>
        {this.state.emailSentAgain &&
          <div className="cps-padding-bottom-16">
            Email was resent!
          </div>
        }
      </React.Fragment>
    )
  }

  renderTimeoutMobile = () => {
    return (
      <ContentWrapperOnly>
        <div className="cps-text-center email-sent-timeout-mobile-view-identifier">
          <img
            src='https://cdn.canopytax.com/static/login-ui/hourglass.svg'
            alt="Hourglass"
          />
          <div className={`cps-title cps-wt-semibold cps-margin-top-32`}>
            You've run out of time
          </div>
          <div className={`cps-subheader-sm cps-margin-top-32`}>
            To make sure your account stays secure we've cancelled this request because it is taking too long to authorize.
          </div>
          <div className={`cps-subheader-sm cps-margin-top-16`}>
            Don't worry though, you can give it another try by logging in again.
          </div>
        </div>
        <CprButton
          className={`${styles.mobileButton}`}
          actionType={`primary`}
          type='button'
          onClick={this.routeToSignIn}
        >
          Return to login
        </CprButton>
      </ContentWrapperOnly>
    )
  }

  renderTimeoutDesktop = () => {
    return (
      <div>
        <span className={`cps-subheader-sm`}>
          It took too long to validate your identity. Please return to sign in and try again.
        </span>
        <div className={`cps-padding-bottom-16`} />
        <CprButton
          actionType={`primary`}
          type='button'
          onClick={this.routeToSignIn}
        >
          Return to sign in
        </CprButton>
      </div>
    )
  }

  routeToSignIn = () => {
    this.props.history.push('/login')
  }

  resendEmailHandler = () => {
    this.setState({emailSentAgain: false})
    this.props.cancelWhenUnmounted(
      resendEmail(this.state.channelId).subscribe(
        data => {
          this.props.cancelWhenUnmounted(
            this.mfaObservableSubscribe(
              concat(
                onPusher('mfa-login', data.body.channel).pipe(
                  first(),
                  tap(() => {
                    this.setState({loadingApp: true})
                  }),
                ),
                loginAfterMFA(data.body.channel),
                defer(Auth.refetchLoggedInUser),
                defer(Auth.refetchTenant),
              )
            )
          )
          this.setState({emailSentAgain: true})
          if (this.props.mobile) {
            this.createMobileEmailSuccessToast();
          }
        },
        asyncStacktrace(err => {
          const status = err.status
          if (status === 401) {
            this.setState({invalidCredentialsCookie: true})
          } else {
            catchSyncStacktrace(err)
          }
        })
      )
    )
  }

  createMobileEmailSuccessToast = () => {
    successToast(`We've resent the verification email. You should receive it shortly.`);
  }
}
